/**
 * Granular permissions and the default role matrix (§6, §29).
 *
 * Roles are stored in the database and editable by a Super Administrator; the
 * map below is what gets seeded and what the tests assert against. Authorisation
 * always checks a *permission*, never a role name, so adding a seventh role
 * later touches no guard code.
 */
export const PERMISSIONS = {
    MEMBERS_VIEW: 'members.view',
    MEMBERS_CREATE: 'members.create',
    MEMBERS_EDIT: 'members.edit',
    MEMBERS_DELETE: 'members.delete',
    MEMBERS_EXPORT: 'members.export',
    KYC_VIEW: 'kyc.view',
    KYC_REVIEW: 'kyc.review',
    KYC_APPROVE: 'kyc.approve',
    KYC_REJECT: 'kyc.reject',
    DOCUMENTS_VIEW: 'documents.view',
    DOCUMENTS_DOWNLOAD: 'documents.download',
    DOCUMENTS_DELETE: 'documents.delete',
    LOAN_PRODUCTS_VIEW: 'loan_products.view',
    LOAN_PRODUCTS_CREATE: 'loan_products.create',
    LOAN_PRODUCTS_EDIT: 'loan_products.edit',
    LOAN_PRODUCTS_DELETE: 'loan_products.delete',
    APPLICATIONS_VIEW: 'applications.view',
    APPLICATIONS_REVIEW: 'applications.review',
    APPLICATIONS_APPROVE: 'applications.approve',
    APPLICATIONS_REJECT: 'applications.reject',
    LOANS_VIEW: 'loans.view',
    LOANS_EDIT: 'loans.edit',
    LOANS_WRITE_OFF: 'loans.write_off',
    LOANS_RESCHEDULE: 'loans.reschedule',
    GUARANTORS_VIEW: 'guarantors.view',
    GUARANTORS_MANAGE: 'guarantors.manage',
    DISBURSEMENTS_VIEW: 'disbursements.view',
    DISBURSEMENTS_CREATE: 'disbursements.create',
    DISBURSEMENTS_APPROVE: 'disbursements.approve',
    REPAYMENTS_VIEW: 'repayments.view',
    REPAYMENTS_CREATE: 'repayments.create',
    REPAYMENTS_EDIT: 'repayments.edit',
    REPAYMENTS_REVERSE: 'repayments.reverse',
    REMINDERS_VIEW: 'reminders.view',
    REMINDERS_MANAGE: 'reminders.manage',
    NOTIFICATIONS_VIEW: 'notifications.view',
    NOTIFICATIONS_SEND: 'notifications.send',
    NOTIFICATION_TEMPLATES_MANAGE: 'notification_templates.manage',
    REPORTS_VIEW: 'reports.view',
    REPORTS_EXPORT: 'reports.export',
    USERS_VIEW: 'users.view',
    USERS_CREATE: 'users.create',
    USERS_EDIT: 'users.edit',
    USERS_DELETE: 'users.delete',
    ROLES_VIEW: 'roles.view',
    ROLES_MANAGE: 'roles.manage',
    AUDIT_VIEW: 'audit.view',
    SETTINGS_VIEW: 'settings.view',
    SETTINGS_MANAGE: 'settings.manage',
};
export const ALL_PERMISSIONS = Object.values(PERMISSIONS);
export const SystemRole = {
    SUPER_ADMINISTRATOR: 'SUPER_ADMINISTRATOR',
    LOAN_ADMINISTRATOR: 'LOAN_ADMINISTRATOR',
    KYC_OFFICER: 'KYC_OFFICER',
    FINANCE_OFFICER: 'FINANCE_OFFICER',
    COMMUNICATION_OFFICER: 'COMMUNICATION_OFFICER',
    MEMBER: 'MEMBER',
};
/** Roles that reach the admin portal at all. Members get the member portal only. */
export const ADMIN_ROLES = [
    SystemRole.SUPER_ADMINISTRATOR,
    SystemRole.LOAN_ADMINISTRATOR,
    SystemRole.KYC_OFFICER,
    SystemRole.FINANCE_OFFICER,
    SystemRole.COMMUNICATION_OFFICER,
];
const P = PERMISSIONS;
export const ROLE_PERMISSIONS = {
    SUPER_ADMINISTRATOR: ALL_PERMISSIONS,
    LOAN_ADMINISTRATOR: [
        P.MEMBERS_VIEW,
        P.KYC_VIEW,
        P.DOCUMENTS_VIEW,
        P.DOCUMENTS_DOWNLOAD,
        P.LOAN_PRODUCTS_VIEW,
        P.APPLICATIONS_VIEW,
        P.APPLICATIONS_REVIEW,
        P.APPLICATIONS_APPROVE,
        P.APPLICATIONS_REJECT,
        P.LOANS_VIEW,
        P.LOANS_EDIT,
        P.LOANS_RESCHEDULE,
        P.GUARANTORS_VIEW,
        P.GUARANTORS_MANAGE,
        P.DISBURSEMENTS_VIEW,
        P.REPAYMENTS_VIEW,
        P.REPAYMENTS_CREATE,
        P.REMINDERS_VIEW,
        P.REMINDERS_MANAGE,
        P.NOTIFICATIONS_VIEW,
        P.NOTIFICATIONS_SEND,
        P.REPORTS_VIEW,
        P.REPORTS_EXPORT,
    ],
    KYC_OFFICER: [
        P.MEMBERS_VIEW,
        P.KYC_VIEW,
        P.KYC_REVIEW,
        P.KYC_APPROVE,
        P.KYC_REJECT,
        P.DOCUMENTS_VIEW,
        P.DOCUMENTS_DOWNLOAD,
        P.NOTIFICATIONS_VIEW,
        P.REPORTS_VIEW,
    ],
    FINANCE_OFFICER: [
        P.MEMBERS_VIEW,
        P.LOANS_VIEW,
        P.GUARANTORS_VIEW,
        P.DISBURSEMENTS_VIEW,
        P.DISBURSEMENTS_CREATE,
        P.DISBURSEMENTS_APPROVE,
        P.REPAYMENTS_VIEW,
        P.REPAYMENTS_CREATE,
        P.REPAYMENTS_EDIT,
        P.REPAYMENTS_REVERSE,
        P.REPORTS_VIEW,
        P.REPORTS_EXPORT,
    ],
    COMMUNICATION_OFFICER: [
        P.MEMBERS_VIEW,
        P.REMINDERS_VIEW,
        P.REMINDERS_MANAGE,
        P.NOTIFICATIONS_VIEW,
        P.NOTIFICATIONS_SEND,
        P.NOTIFICATION_TEMPLATES_MANAGE,
        P.REPORTS_VIEW,
    ],
    // Members hold no admin permissions. Their access to their own records is
    // enforced by ownership checks in the service layer, not by this matrix.
    MEMBER: [],
};
export function hasPermission(granted, required) {
    const needed = Array.isArray(required) ? required : [required];
    const set = new Set(granted);
    return needed.every((permission) => set.has(permission));
}
export function hasAnyPermission(granted, required) {
    const set = new Set(granted);
    return required.some((permission) => set.has(permission));
}
/** Groups permissions by resource for the role editor UI. */
export function groupPermissions(permissions = ALL_PERMISSIONS) {
    return permissions.reduce((groups, permission) => {
        const [resource = 'other'] = permission.split('.');
        (groups[resource] ??= []).push(permission);
        return groups;
    }, {});
}
//# sourceMappingURL=permissions.js.map